import jwtDecode from "jwt-decode";

/**
 * Auth0 custom claim key where the user's upstream identity provider access
 * token is embedded in the Superblocks-issued Auth0 JWT.
 */
export const IDP_TOKEN_KEY = "https://superblocks/api/idp_token";

/**
 * Superblocks JWT claim holding the same provider token, except the server
 * re-mints this one out of the Token Vault on every mint. The Auth0 claim above
 * is only ever the token from the original login, so it goes stale within the
 * hour and nothing the user does short of logging in again moves it.
 */
export const FEDERATED_TOKEN_KEY = "federated_token";

/**
 * Extract the raw IdP token string from a Superblocks Auth0 JWT, or
 * `undefined` if the outer JWT cannot be decoded or the claim is missing.
 */
export function extractIdpToken(auth0Jwt: string): string | undefined {
  try {
    const decoded: Record<string, unknown> = jwtDecode(auth0Jwt);
    const idpToken = decoded[IDP_TOKEN_KEY];
    return typeof idpToken === "string" ? idpToken : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Result of inspecting the IdP token the browser holds for the current user.
 *
 * The shape matches the contract we enforce end-to-end: the UI should only
 * block locally on things it can actually observe (no token, or a JWT that is
 * definitely expired). Opaque tokens (e.g. Google `ya29.*` access tokens) are
 * forwarded to the server and validated there when the relevant feature flag
 * is enabled on the orchestrator
 * (`agent.plugins.auth.validate_subject_token_during_obo_flow.enabled`).
 */
export type IdpTokenInspection =
  | { present: false }
  | { present: true; format: "opaque" }
  | { present: true; format: "jwt"; expired: boolean };

/**
 * Inspect the provider token carried in one claim of an outer JWT. Pure function
 * — accepts the outer JWT string and returns a structured description of the
 * token it carries without ever throwing.
 */
export function inspectIdpTokenInJwt(
  outerJwt: string | null | undefined,
  claimKey: string,
): IdpTokenInspection {
  if (!outerJwt) {
    return { present: false };
  }

  let decodedOuterJwt: Record<string, unknown>;
  try {
    decodedOuterJwt = jwtDecode(outerJwt);
  } catch {
    return { present: false };
  }

  const idpToken = decodedOuterJwt[claimKey];
  if (typeof idpToken !== "string" || idpToken.length === 0) {
    return { present: false };
  }

  let decodedIdpToken: Record<string, unknown>;
  try {
    decodedIdpToken = jwtDecode(idpToken);
  } catch {
    // Not a JWT (e.g. a Google `ya29.*` opaque access token). We cannot
    // inspect its expiry locally; forward to the server for validation.
    return { present: true, format: "opaque" };
  }

  const exp = decodedIdpToken.exp;
  // A JWT without `exp` is treated as non-expired locally; any expiry
  // enforcement is the server's responsibility.
  const expired = typeof exp === "number" ? exp < Date.now() / 1000 : false;
  return { present: true, format: "jwt", expired };
}
